import type { TriMesh } from "../../../../domain/utilities/Tmesh";

function ifcReal(n: number, decimals = 12): string {
  if (!Number.isFinite(n)) return "0.";
  let s = n.toFixed(decimals).replace(/0+$/, "");
  if (s.endsWith(".")) return s;
  if (!s.includes(".")) return s + ".";
  return s;
}

function ifcList(items: string[]): string {
  return `(${items.join(",")})`;
}

export function createBodyTessellationFromMeshes(args: {
  getNextId: () => number;
  geometricContextId: number;   // you currently pass this in
  meshes: TriMesh[];
  wrapInProductShape?: boolean;
}) {
  const { getNextId, geometricContextId, meshes } = args;
  const wrapInProductShape = args.wrapInProductShape ?? true;
  let output = "";

  const add = (rhs: string): number => {
    const id = getNextId();
    output += `#${id}=${rhs}\n`;
    return id;
  };

  const faceSetIds: number[] = [];

  const addSurfaceStyleForFaceSet = (faceSetId: number, color: NonNullable<TriMesh["color"]>) => {
    const r = ifcReal(color.r, 6);
    const g = ifcReal(color.g, 6);
    const b = ifcReal(color.b, 6);
    const colourId = add(`IFCCOLOURRGB($,${r},${g},${b});`);

    const transparency = color.a !== undefined ? 1 - Math.min(Math.max(color.a, 0), 1) : undefined;
    const shadingId = transparency !== undefined
      ? add(`IFCSURFACESTYLESHADING(#${colourId},${ifcReal(transparency, 6)});`)
      : add(`IFCSURFACESTYLESHADING(#${colourId},$);`);

    const surfaceStyleId = add(`IFCSURFACESTYLE($,.BOTH.,(#${shadingId}));`);
    // Attach style directly to each face set because different meshes (or split color
    // buckets from one source polyface) may carry distinct colors.
    add(`IFCSTYLEDITEM(#${faceSetId},(#${surfaceStyleId}),$);`);
  };

  for (const mesh of meshes) {
    // PointList
    const pointRows = mesh.points.map(([x, y, z]: [number, number, number]) =>
      ifcList([ifcReal(x), ifcReal(y), ifcReal(z)])
    );

    const pointListId = add(`IFCCARTESIANPOINTLIST3D(${ifcList(pointRows)},$);`);

    // Triangles (IFC is 1-based)
    const triRows = mesh.triangles.map(([a, b, c]) =>
      ifcList([String(a + 1), String(b + 1), String(c + 1)])
    );

    const faceSetId = add(`IFCTRIANGULATEDFACESET(#${pointListId},$,$,${ifcList(triRows)},$);`);
    if (mesh.color) addSurfaceStyleForFaceSet(faceSetId, mesh.color);
    faceSetIds.push(faceSetId);
  }

  // ShapeRepresentation (Body / Tessellation)
  const items = ifcList(faceSetIds.map((id) => `#${id}`));
  const shapeRepId = add(`IFCSHAPEREPRESENTATION(#${geometricContextId},'Body','Tessellation',${items});`);

  // ProductDefinitionShape
  if (!wrapInProductShape) {
    return { output, shapeRepId };
  }
  const productShapeId = add(`IFCPRODUCTDEFINITIONSHAPE($,$,(#${shapeRepId}));`);

  return { output, shapeRepId, productShapeId };
}
